import { Link, useNavigate } from "react-router-dom";
import { FixtureList } from "../../components/FixtureList.js";
import { TeamSheets } from "../../components/TeamSheets.js";
import { hasRole, useAuth } from "../../lib/auth.js";
import { isPlayable } from "../../lib/draw.js";
import type { TournamentEvent } from "../../lib/types.js";
import { EventGate, EventScreen, rosterOf, useDraw, useTieTeams, type Roster } from "./shared.js";

/**
 * Who plays whom, in the order it is to be played.
 *
 * Read-only apart from one thing: the way in to scoring the match you are in. A player is
 * offered it on their own fixtures, an organiser on all of them — the organiser is usually
 * the one holding the clipboard for people who never open the app.
 */
export function EventDraw() {
  return <EventGate>{({ event }) => <Draw event={event} />}</EventGate>;
}

function Draw({ event }: { event: TournamentEvent }) {
  const { member } = useAuth();
  const memberId = member?.id ?? null;
  const canOrganise = hasRole(member, "organiser");
  const navigate = useNavigate();

  const { data: fixtures = [], isPending } = useDraw(event.id);
  const { data: teams } = useTieTeams(event);
  // The organiser's seeds, beside the names they belong to, the way a printed draw shows them.
  // Here only: the board and the standings are read from across a hall and want just the name.
  const seedOf = new Map(
    event.entries
      .filter((entry) => entry.seed != null)
      .map((entry) => [entry.memberId, entry.seed]),
  );
  const roster: Roster = new Map(
    [...rosterOf(event)].map(([id, person]) => [
      id,
      seedOf.has(id) ? { ...person, name: `${person.name} [${seedOf.get(id)}]` } : person,
    ]),
  );

  const played = fixtures.filter((fixture) => fixture.result).length;
  const mine = (fixture: { playersA?: string[] | null; playersB?: string[] | null }) =>
    memberId !== null &&
    (fixture.playersA?.includes(memberId) === true ||
      fixture.playersB?.includes(memberId) === true);

  return (
    <EventScreen event={event} title="Draw">
      {teams?.length ? (
        <section className="mb-8">
          <h2 className="micro mb-3">Teams</h2>
          <TeamSheets teams={teams} roster={roster} />
        </section>
      ) : null}

      {fixtures.length === 0 ? (
        <p className="text-sm text-ink-muted">
          {isPending
            ? "Loading…"
            : canOrganise
              ? "Nothing drawn yet."
              : "Nothing drawn yet. The organiser makes the draw once entries are in."}
        </p>
      ) : (
        <>
          <p className="micro mb-1">
            {played} of {fixtures.length} played
            {/* Once for the whole draw, rather than "unrated" on every row of it. */}
            {event.countsForRating ? null : " · doesn't count towards ratings"}
          </p>
          <FixtureList
            fixtures={fixtures}
            roster={roster}
            eventCountsForRating={event.countsForRating}
            canScore={(fixture) => isPlayable(fixture) && (canOrganise || mine(fixture))}
            onScore={(fixture) => navigate(`/events/${event.id}/score/${fixture.id}`)}
            // Mid-tournament repairs happen here rather than behind the manage screen: the
            // organiser is looking at the draw when somebody limps off it.
            onEdit={
              canOrganise
                ? (fixture) => navigate(`/events/${event.id}/fixture/${fixture.id}`)
                : undefined
            }
          />
        </>
      )}

      <div className="mt-6 flex flex-wrap gap-4">
        {/* Everybody's, not the organiser's: whoever is nearest the television is the one
            who puts it on. */}
        {fixtures.length ? (
          <Link to={`/events/${event.id}/board`} className="micro underline">
            Court board
          </Link>
        ) : null}
        {/* One more game, without redrawing the evening. Not for a knockout, where a match on
            the end of the bracket belongs to nothing. */}
        {canOrganise && fixtures.length > 0 && event.format !== "knockout" ? (
          <Link to={`/events/${event.id}/fixture/new`} className="micro underline">
            Add a match
          </Link>
        ) : null}
        {canOrganise ? (
          <Link to={`/events/${event.id}/manage`} className="micro underline">
            {fixtures.length ? "Redraw or edit the teams" : "Make the draw"}
          </Link>
        ) : null}
      </div>
    </EventScreen>
  );
}
